import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "@/lib/api";
import {
  LayoutDashboard, BookOpen, FileText, ShoppingBag, PackageOpen, Landmark,
  BarChart3, ShieldCheck, ShieldAlert, LayoutTemplate, Settings, Users, Truck, Sparkles,
  ScrollText, Coins, Building2, Factory, Receipt, Crown, ClipboardList, Target,
  Repeat, Cpu, ClipboardCheck, Activity, LineChart,
  Calculator, Lightbulb, Tv, Car, ArrowDownToLine, Layers, FileMinus,
  Boxes, Wrench, Award, ChevronDown, ChevronRight, HelpCircle, Network,
  Wallet, Cog, FileCheck, CalendarClock, Zap, XCircle, Sliders, Palette, FileSignature,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import OrgSwitcher from "@/components/OrgSwitcher";

// ─── NEW GROUPING per Phase-1 brief ────────────────────────────────
// Feb 2026 · dedup + consolidation:
//   - `distribution` was in BOTH Sales AND its own section → keep only in Sales
//   - `approvals` was in BOTH Procurement AND its own section → keep only own section
//   - `Fixed Assets` singleton merged into Accounting
//   - `Subscription & Plan` singleton merged into Company & Setup
const SECTIONS = [
  { label: "Overview", module: null, items: [
    { to: "/app/dashboard", icon: LayoutDashboard, key: "dashboard" },
    { to: "/app/ai", icon: Sparkles, key: "ai_assistant" },
    { to: "/app/tickets", icon: HelpCircle, key: "support_tickets" },
  ]},
  { label: "Sales", module: "accounts", items: [
    { to: "/app/sales-workflow", icon: ClipboardList, key: "sales_wf" },
    { to: "/app/sales", icon: FileText, key: "sales" },
    { to: "/app/credit-notes", icon: FileMinus, key: "credit_notes" },
    { to: "/app/customers", icon: Users, key: "customers" },
    { to: "/app/recurring", icon: Repeat, key: "recurring" },
    { to: "/app/distribution", icon: Network, key: "distribution" },
  ]},
  { label: "Procurement", module: "purchases", items: [
    { to: "/app/purchase-workflow", icon: ClipboardList, key: "purchase_wf" },
    { to: "/app/purchases", icon: ShoppingBag, key: "purchases" },
    { to: "/app/purchase-return", icon: FileMinus, key: "purchase_return" },
    { to: "/app/debit-notes", icon: FileMinus, key: "debit_notes" },
    { to: "/app/vendors", icon: Truck, key: "vendors" },
    { to: "/app/gate-pass?direction=inward", icon: ShieldCheck, key: "gate_pass_inward", requires: "gate_pass" },
    { to: "/app/gate-inward", icon: Truck, key: "gate_inward", requires: "procurement" },
    { to: "/app/stores-queue", icon: Boxes, key: "stores_queue", requires: "procurement" },
  ]},
  { label: "Inventory", module: null, items: [
    { to: "/app/inventory", icon: Boxes, key: "inventory" },
  ]},
  { label: "Manufacturing", module: null, items: [
    { to: "/app/bom", icon: Factory, key: "bom", requires: "bom" },
    { to: "/app/bom-issue", icon: Factory, key: "bom_issue" },
    { to: "/app/bom-receive", icon: Factory, key: "bom_receive" },
    { to: "/app/mfg-masters", icon: Cpu, key: "mfg_masters", requires: "mfg_masters" },
    { to: "/app/production", icon: Activity, key: "production", requires: "production" },
    { to: "/app/mrp", icon: PackageOpen, key: "mrp", requires: "mrp" },
    { to: "/app/maintenance", icon: Wrench, key: "maintenance", requires: "production" },
    { to: "/app/qc", icon: ShieldCheck, key: "qc_dashboard" },
    { to: "/app/quality", icon: ClipboardCheck, key: "quality", requires: "quality" },
    { to: "/app/procurement", icon: Award, key: "procurement", requires: "procurement" },
    { to: "/app/costing", icon: Calculator, key: "costing", requires: "costing" },
    { to: "/app/npd", icon: Lightbulb, key: "npd", requires: "npd" },
    { to: "/shopfloor", icon: LineChart, key: "shopfloor", requires: "production" },
    { to: "/andon", icon: Tv, key: "andon", requires: "production" },
  ]},
  { label: "Dealership", module: null, items: [
    { to: "/app/dealership", icon: Car, key: "dealership", requires: "dealership" },
    { to: "/app/van-sales", icon: Truck, key: "van_sales", requires: "dealership" },
  ]},
  { label: "Job Work", module: "accounts", items: [
    { to: "/app/job-work", icon: Wrench, key: "job_work" },
    { to: "/app/gate-pass?direction=jobwork", icon: ShieldCheck, key: "gate_pass_jobwork", requires: "gate_pass" },
  ]},
  { label: "Banking", module: "accounts", items: [
    { to: "/app/payments", icon: Coins, key: "payments" },
    { to: "/app/payment-sheet", icon: FileCheck, key: "payment_sheet" },
    { to: "/app/reconcile", icon: Building2, key: "reconcile" },
    { to: "/app/bank-rules", icon: Zap, key: "bank_rules" },
    { to: "/app/brs-review", icon: ClipboardCheck, key: "brs_review" },
  ]},
  { label: "Accounting", module: "accounts", items: [
    { to: "/app/accounts", icon: BookOpen, key: "accounts" },
    { to: "/app/vouchers", icon: ScrollText, key: "vouchers" },
    { to: "/app/prepaid", icon: CalendarClock, key: "prepaid" },
    { to: "/app/assets", icon: Landmark, key: "fixed_assets" },
    { to: "/app/approvals", icon: ClipboardCheck, key: "approvals" },
  ]},
  { label: "GST", module: "accounts", items: [
    { to: "/app/reports/gst-reports",       icon: ShieldCheck, key: "gst_reports" },
    { to: "/app/gstr1",                     icon: Sparkles,    key: "gstr1" },
    { to: "/app/ims",                       icon: Repeat,      key: "ims" },
    { to: "/app/gstr3b",                    icon: FileCheck,   key: "gstr3b" },
    { to: "/app/compliance/gstr6",          icon: FileText,    key: "gstr6_compliance" },
    { to: "/app/compliance/gstr9",          icon: ClipboardList, key: "gstr9_compliance" },
    { to: "/app/compliance/gstr9c",         icon: ScrollText,  key: "gstr9c_compliance" },
    { to: "/app/compliance/itc-04",         icon: BarChart3,   key: "itc04_compliance" },
    { to: "/app/reports/rcm-register",      icon: ShieldCheck, key: "rcm_register" },
    { to: "/app/reports/rule37-reversal",   icon: Receipt,     key: "rule37_reversal" },
    { to: "/app/reports/ineligible-itc",    icon: ShieldAlert, key: "ineligible_itc" },
    { to: "/app/reports/group-filing",      icon: ClipboardList, key: "group_filing_dashboard" },
  ]},
  { label: "Other Compliance", module: "accounts", items: [
    { to: "/app/compliance",                icon: ShieldCheck, key: "compliance_hub" },
    { to: "/app/tds",                       icon: Receipt,     key: "tds" },
    { to: "/app/tax-audit",                 icon: ScrollText,  key: "tax_audit" },
    { to: "/app/itr",                       icon: FileText,    key: "itr" },
    { to: "/app/corporate-compliance",      icon: Landmark,    key: "corporate_compliance" },
    { to: "/app/o2c-compliance",            icon: ShieldCheck, key: "o2c_compliance" },
    { to: "/app/esign",                     icon: FileSignature, key: "esign" },
    { to: "/app/compliance-calendar",       icon: Activity,    key: "compliance_calendar" },
  ]},
  { label: "Reports & MIS", module: "accounts", items: [
    { to: "/app/reports", icon: BarChart3, key: "reports" },
    { to: "/app/statement-of-account", icon: FileText, key: "statement_of_account" },
    { to: "/app/mis", icon: LineChart, key: "segment_mis" },
    { to: "/app/mis-pack", icon: BarChart3, key: "mis_pack" },
    { to: "/app/consolidation", icon: Building2, key: "consolidation" },
    { to: "/app/advance-tax", icon: Calculator, key: "advance_tax" },
    { to: "/app/budgets", icon: Target, key: "budgets" },
    { to: "/app/msme-report", icon: ClipboardList, key: "msme_report" },
    { to: "/app/reports/fema-realisation", icon: ShieldCheck, key: "fema_realisation" },
    { to: "/app/reports/as11-restatement", icon: Coins, key: "as11_restatement" },
    { to: "/app/reports/working-capital", icon: Wallet, key: "working_capital" },
    { to: "/app/reports/fa-simulator", icon: Cog, key: "fa_simulator" },
  ]},
  { label: "Company & Setup", module: "accounts", items: [
    { to: "/app/settings", icon: Settings, key: "settings" },
    { to: "/app/settings/appearance", icon: Palette, key: "appearance" },
    { to: "/app/user-guide", icon: BookOpen, key: "user_guide" },
    { to: "/app/subscription", icon: Crown, key: "subscription" },
    { to: "/app/settings/company-gstins", icon: ShieldCheck, key: "company_gstins" },
    { to: "/app/tally-migrator", icon: ArrowDownToLine, key: "tally_migrator" },
    { to: "/app/zoho-migrator", icon: ArrowDownToLine, key: "zoho_migrator" },
    { to: "/app/migrator-mapper", icon: ArrowDownToLine, key: "migrator_mapper" },
    { to: "/app/import", icon: ScrollText, key: "import" },
    { to: "/app/templates", icon: LayoutTemplate, key: "templates" },
    { to: "/app/audit", icon: ShieldCheck, key: "audit" },
    { to: "/app/admin/portal-features", icon: Sliders, key: "portal_features" },
    { to: "/app/admin/branding", icon: Palette, key: "branding" },
  ]},
];

export default function Sidebar() {
  const t = useT();
  const { org, user } = useAuth();
  const { hasModule } = useSubscription();
  const [industryFeatures, setIndustryFeatures] = useState(null);
  const [featureToggles, setFeatureToggles] = useState({});
  const [portalToggles, setPortalToggles] = useState({});
  // Feb-2026 · Founder-signed feature matrix (business_type × feature code)
  const [btSections, setBtSections] = useState({});     // {A:{visible:true},…}
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      import("@/lib/api").then(({ default: api }) => {
        api.get("/industry-features").then(r => {
          if (!cancelled) {
            setIndustryFeatures(r.data?.features || null);
            setFeatureToggles(r.data?.feature_toggles || {});
            setPortalToggles(r.data?.portal_feature_toggles || {});
          }
        }).catch(() => {});
        // Business-type applicability matrix
        api.get("/business-type-features").then(r => {
          if (!cancelled) setBtSections(r.data?.sections || {});
        }).catch(() => {});
      });
    };
    load();
    // Iter-170 · Refetch immediately when the admin saves feature toggles
    const onUpdate = () => load();
    window.addEventListener("feature-toggles-updated", onUpdate);
    return () => { cancelled = true; window.removeEventListener("feature-toggles-updated", onUpdate); };
  }, [org?.id, org?.is_dealership, org?.industry_type, org?.business_type]);
  const [slaBadge, setSlaBadge] = useState(0);
  // Collapsed sections — persist per user in localStorage.
  const _key = `zenith_sidebar_collapsed_${user?.id || "anon"}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(_key) || "[]")); } catch { return new Set(); }
  });
  const toggle = (label) => setCollapsed((cur) => {
    const next = new Set(cur);
    if (next.has(label)) next.delete(label); else next.add(label);
    try { localStorage.setItem(_key, JSON.stringify([...next])); } catch {}
    return next;
  });

  useEffect(() => {
    let cancelled = false;
    const fetchBadge = async () => {
      try {
        const r = await api.get("/procurement/prs/sla-breached");
        if (!cancelled) setSlaBadge(r.data?.count || 0);
      } catch { if (!cancelled) setSlaBadge(0); }
    };
    fetchBadge();
    const t = setInterval(fetchBadge, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [org?.id]);

  return (
    <aside className="hidden lg:flex flex-col h-full w-64 border-r border-white/10 bg-[#0A0A0F] text-white/85" data-testid="sidebar">
      <div className="px-5 pt-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2 mb-1">
          <img src={org?.logo_base64 || org?.logo_url || "/zenith360-logo.png"} alt={org?.name || "Zenith 360"}
                 className="w-9 h-9 rounded-sm object-cover shadow-lg bg-white/10"
                 onError={(e) => { e.currentTarget.src = "/zenith360-logo.png"; }}
                 data-testid="sidebar-logo" />
          <div>
            <div className="font-heading font-bold text-[15px] leading-none tracking-tight truncate max-w-[150px]">{org?.name?.split(" ").slice(0,3).join(" ") || "Zenith 360"}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-0.5">ERP · v1.4</div>
          </div>
        </div>
        {org && <OrgSwitcher org={org} />}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 space-y-5">
        {SECTIONS.map((sec) => {
          // Feb 2026 · Industry-based gating
          const f = industryFeatures || {};
          const invHidden = f.inventory === false || btSections.F?.visible === false;
          if (sec.label === "Inventory" && invHidden) {
            return (
              <div key="items-services" className="px-3">
                <div className="px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Items</div>
                <NavLink to="/app/inventory?mode=services" data-testid="nav-items-services"
                  className={() => `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${window.location.pathname === "/app/inventory" ? "bg-white/10 text-white border-l-2 border-orange-500 pl-[10px]" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                  <Boxes className="w-4 h-4 shrink-0" /><span className="flex-1">Items &amp; Services</span>
                </NavLink>
              </div>
            );
          }
          if (sec.label === "Manufacturing" && f.manufacturing === false) return null;
          if (sec.label === "Dealership"    && f.dealership   === false) return null;
          if (sec.label === "Distribution"  && f.dealership   === false && f.inventory === false) return null;
          if (sec.module && !hasModule(sec.module)) return null;

          // Feb-2026 · Founder-signed business-type matrix — hide the whole
          // section if every feature in it is set to N for this tenant.
          const SECTION_KEY = { Inventory: "F", Manufacturing: "G", Dealership: "J" };
          const secKey = SECTION_KEY[sec.label];
          if (secKey && btSections[secKey] && btSections[secKey].visible === false) return null;

          // Iter-154 · Expand the Dealership section into individual sub-modules
          // (Leads · Quotations · Bookings · …) when the org has the Dealership
          // add-on. Each item deep-links to /app/dealership?tab=<value>.
          if (sec.label === "Dealership" && org?.is_dealership) {
            const tg = featureToggles || {};
            const dealerItems = [
              { to: "/app/dealership",                              icon: LayoutDashboard, key: "dealership",           label: "Dealership Dashboard" },
              { to: "/app/dealership?tab=leads",                    icon: Target,          key: "dealer_leads",         label: "Leads" },
              { to: "/app/dealership?tab=quotations",               icon: FileText,        key: "dealer_quotations",    label: "Quotations" },
              { to: "/app/dealership?tab=bookings",                 icon: ClipboardList,   key: "dealer_bookings",      label: "Bookings" },
              tg.cancellations       !== false && { to: "/app/dealership?tab=cancellations", icon: XCircle,       key: "dealer_cancellations", label: "Cancellations" },
              tg.finance             !== false && { to: "/app/dealership?tab=finance",       icon: Wallet,        key: "dealer_finance",       label: "Finance" },
              tg.insurance           !== false && { to: "/app/dealership?tab=insurance",     icon: ShieldCheck,   key: "dealer_insurance",     label: "Insurance" },
              tg.rto                 !== false && { to: "/app/dealership?tab=rto",           icon: FileCheck,     key: "dealer_rto",           label: "RTO" },
              tg.discount_approvals  !== false && { to: "/app/dealership?tab=approvals",     icon: ClipboardCheck,key: "dealer_approvals",     label: "Discount Approvals" },
              { to: "/app/dealership?tab=stock",                    icon: Boxes,           key: "dealer_stock",         label: "Vehicle Stock" },
              tg.price_lists         !== false && { to: "/app/dealership?tab=price-lists",   icon: Layers,        key: "dealer_price_lists",   label: "Price Lists" },
              tg.waitlist            !== false && { to: "/app/dealership?tab=waitlist",      icon: CalendarClock, key: "dealer_waitlist",      label: "Waitlist" },
              tg.deliveries          !== false && { to: "/app/dealership?tab=deliveries",    icon: Truck,         key: "dealer_deliveries",    label: "Deliveries" },
              tg.deliveries          !== false && { to: "/app/gate-pass?direction=outward",   icon: ShieldCheck,   key: "gate_pass_outward",    label: "Outward Gate Pass" },
              tg.cash_desk           !== false && { to: "/app/dealership?tab=cash-desk",     icon: Coins,         key: "dealer_cash_desk",     label: "Cash Desk" },
              tg.service_desk        !== false && { to: "/app/dealership?tab=service-desk",  icon: Wrench,        key: "dealer_service_desk",  label: "Service Desk" },
              { to: "/app/dealership?tab=masters",                  icon: Cpu,             key: "dealer_masters",       label: "Masters" },
              tg.van_sales           !== false && { to: "/app/van-sales",                     icon: Truck,         key: "van_sales",            label: "Van Sales" },
            ].filter(Boolean);
            const isCollapsed = collapsed.has(sec.label);
            return (
              <div key={sec.label} className="px-3">
                <button onClick={() => toggle(sec.label)} data-testid="nav-group-dealership"
                  className="w-full flex items-center gap-1 px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors group">
                  {isCollapsed ? <ChevronRight className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />}
                  <span className="flex-1 text-left">Dealership 360</span>
                  <span className="text-white/20 group-hover:text-white/40 text-[9px]">{dealerItems.length}</span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {dealerItems.map((it) => {
                      // Iter-155 · Portal-wide feature toggle can hide any
                      // dealership sub-module here too.
                      if (portalToggles[it.key] === false) return null;
                      const Icon = it.icon;
                      const [pathname, search] = it.to.split("?");
                      return (
                        <NavLink key={it.key} to={it.to} end={!search} data-testid={`nav-${it.key}`}
                          className={({ isActive }) => {
                            let active = isActive;
                            if (search) {
                              const cur = new URLSearchParams(window.location.search).get("tab");
                              const want = new URLSearchParams(search).get("tab");
                              active = (window.location.pathname === pathname) && cur === want;
                            } else if (window.location.pathname === pathname) {
                              active = !window.location.search.includes("tab=");
                            }
                            return `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${active ? "bg-white/10 text-white border-l-2 border-orange-500 pl-[10px]" : "text-white/60 hover:text-white hover:bg-white/5"}`;
                          }}>
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1">{it.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Standard section rendering
          const isCollapsed = collapsed.has(sec.label);
          return (
            <div key={sec.label} className="px-3">
              <button onClick={() => toggle(sec.label)} data-testid={`nav-group-${sec.label.toLowerCase().replace(/\s+/g,'-')}`}
                className="w-full flex items-center gap-1 px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors group">
                {isCollapsed ? <ChevronRight className="w-3 h-3 shrink-0 transition-transform" /> : <ChevronDown className="w-3 h-3 shrink-0 transition-transform" />}
                <span className="flex-1 text-left">{sec.label}</span>
                <span className="text-white/20 group-hover:text-white/40 text-[9px]">{sec.items.length}</span>
              </button>
              {!isCollapsed && (
                <div className="space-y-0.5">
                  {sec.items.map((it) => {
                    if (it.requires && !hasModule(it.requires)) return null;
                    // Iter-153 · Per-company feature toggles (Dealership add-on)
                    if (org?.is_dealership) {
                      if (it.key === "distribution" && featureToggles.distribution === false) return null;
                      if (it.key === "van_sales"    && featureToggles.van_sales === false) return null;
                    }
                    // Iter-155 · Portal-wide feature toggles (Admin Settings)
                    // Any item can be flipped off — except the panel itself and
                    // the platform-safety anchors handled server-side.
                    if (portalToggles[it.key] === false) return null;
                    const Icon = it.icon;
                    const badge = it.key === "approvals" && slaBadge > 0 ? slaBadge : 0;
                    return (
                      <NavLink key={it.to} to={it.to} end data-testid={`nav-${it.key}`}
                                 className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${isActive ? "bg-white/10 text-white border-l-2 border-orange-500 pl-[10px]" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="flex-1">{t(it.key)}</span>
                        {badge > 0 && (
                          <span className="ml-auto text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white animate-pulse"
                                    data-testid={`nav-badge-${it.key}`}>
                            {badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 px-4 py-3 text-xs">
        <NavLink to="/app/help" className="flex items-center gap-2 px-2 py-2 mb-2 rounded-sm text-white/70 hover:text-white hover:bg-white/5 border border-white/10 hover:border-orange-500/40 transition-colors group" data-testid="nav-help">
          <HelpCircle className="w-3.5 h-3.5 shrink-0 group-hover:text-orange-500" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium leading-tight">Help &amp; Docs</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">searchable · in-app</div>
          </div>
        </NavLink>
        <a href="/api/brochure/product-guide.pdf" target="_blank" rel="noopener"
           className="flex items-center gap-2 px-2 py-2 mb-2 rounded-sm text-white/70 hover:text-white hover:bg-white/5 border border-white/10 hover:border-orange-500/40 transition-colors group"
           data-testid="download-brochure-link">
          <FileText className="w-3.5 h-3.5 shrink-0 group-hover:text-orange-500" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium leading-tight">Product Brochure</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">PDF · client-ready</div>
          </div>
          <ArrowDownToLine className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm flex items-center justify-center font-semibold text-white text-xs"
               style={{ background: "linear-gradient(135deg,#2E63FF,#F97316)" }}>
            {(user?.full_name || "U")[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate text-white">{user?.full_name}</div>
            <div className="text-[10px] font-mono uppercase text-white/40 tracking-widest">{user?.role?.replace("_", " ")}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
